// Modal shown inline by any flow that hits DeviceLockedError mid-op.
// The Service Worker holds the master key in a module-level variable
// and can be evicted by the browser after ~30s of idle; after that,
// prepareDeviceArgs throws DeviceLockedError. This dialog asks the
// user to re-authenticate (passphrase and/or passkey, whichever is
// configured on the current WasmDeviceRecord), restores the master
// key to the SW, and calls onDone so the caller can retry its op.

import React, { useEffect, useState } from 'react'
import { loadCurrentWasmDevice, WasmDeviceRecord } from './deviceStore'
import { deviceWorkerClient } from './deviceWorkerClient'
import { loadPracticemdWasm, PracticemdWASM } from './loader'
import { unlockViaAuthenticator } from './authenticators'
import { bytesToHex } from './importFromLegacy'

export interface ReUnlockDialogProps {
    /** Called after the SW has been unlocked. Caller should retry. */
    onDone: () => void
    onCancel: () => void
}

const overlayStyle: React.CSSProperties = {
    position: 'fixed',
    inset: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'rgba(14, 15, 20, 0.75)',
    padding: '2rem',
    zIndex: 9999,
    overflow: 'auto',
}

const panelStyle: React.CSSProperties = {
    maxWidth: 440,
    width: '100%',
    background: '#1a1b22',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    borderRadius: 12,
    padding: '1.75rem',
    color: '#fff',
}

const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: '0.5rem 0.75rem',
    background: 'rgba(255, 255, 255, 0.05)',
    border: '1px solid rgba(255, 255, 255, 0.15)',
    borderRadius: 6,
    color: '#fff',
    fontFamily: 'inherit',
    fontSize: '0.9rem',
}

const labelStyle: React.CSSProperties = {
    display: 'block',
    color: 'rgba(255, 255, 255, 0.7)',
    fontSize: '0.85rem',
    marginBottom: '0.4rem',
}

const primaryBtn: React.CSSProperties = {
    background: '#4285F4',
    color: '#fff',
    border: 'none',
    borderRadius: 6,
    padding: '0.55rem 1.1rem',
    cursor: 'pointer',
    fontSize: '0.9rem',
}

const secondaryBtn: React.CSSProperties = {
    background: 'transparent',
    color: 'rgba(255, 255, 255, 0.7)',
    border: '1px solid rgba(255, 255, 255, 0.15)',
    borderRadius: 6,
    padding: '0.55rem 1.1rem',
    cursor: 'pointer',
    fontSize: '0.9rem',
}

export const ReUnlockDialog: React.FC<ReUnlockDialogProps> = ({ onDone, onCancel }) => {
    const [record, setRecord] = useState<WasmDeviceRecord | null>(null)
    const [wasm, setWasm] = useState<PracticemdWASM | null>(null)
    const [loadErr, setLoadErr] = useState('')
    const [passphrase, setPassphrase] = useState('')
    const [busy, setBusy] = useState<'' | 'passphrase' | 'passkey'>('')
    const [err, setErr] = useState('')

    useEffect(() => {
        let cancelled = false
        loadCurrentWasmDevice()
            .then(r => { if (!cancelled) setRecord(r) })
            .catch(e => { if (!cancelled) setLoadErr(e instanceof Error ? e.message : String(e)) })
        loadPracticemdWasm()
            .then(w => { if (!cancelled) setWasm(w) })
            .catch(e => { if (!cancelled) setLoadErr(e instanceof Error ? e.message : String(e)) })
        return () => { cancelled = true }
    }, [])

    const canPassphrase = !!record?.passphraseEnvelopeB64
    const canPasskey = !!record?.authenticators.length

    async function runPassphrase() {
        if (!wasm || !record?.passphraseEnvelopeB64) return
        if (!passphrase) {
            setErr('Enter your passphrase.')
            return
        }
        setBusy('passphrase')
        setErr('')
        try {
            const mk = await wasm.unlockMasterKey(record.passphraseEnvelopeB64, passphrase)
            await deviceWorkerClient.unlock(mk)
            setPassphrase('')
            onDone()
        } catch (e) {
            setErr(e instanceof Error ? e.message : String(e))
        } finally {
            setBusy('')
        }
    }

    async function runPasskey() {
        if (!record?.authenticators.length) return
        setBusy('passkey')
        setErr('')
        try {
            const mkBytes = await unlockViaAuthenticator(record.authenticators)
            try {
                await deviceWorkerClient.unlock(bytesToHex(mkBytes))
            } finally {
                mkBytes.fill(0)
            }
            onDone()
        } catch (e) {
            setErr(e instanceof Error ? e.message : String(e))
        } finally {
            setBusy('')
        }
    }

    return (
        <div style={overlayStyle}>
            <div style={panelStyle}>
                <h3 style={{ marginTop: 0, marginBottom: '0.5rem' }}>Unlock to continue</h3>
                <p style={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '0.9rem', marginTop: 0 }}>
                    We automatically lock your device during inactivity so your
                    encrypted data stays protected. Unlock to pick back up.
                </p>

                {loadErr && (
                    <div style={{ color: '#ff9f9f', fontSize: '0.85rem', marginBottom: '1rem' }}>
                        {loadErr}
                    </div>
                )}

                {!record && !loadErr && (
                    <div style={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '0.85rem' }}>
                        Loading device…
                    </div>
                )}

                {record && !canPassphrase && !canPasskey && (
                    <div style={{ color: '#ff9f9f', fontSize: '0.85rem' }}>
                        No unlock methods are configured on this device.
                    </div>
                )}

                {canPassphrase && (
                    <div style={{ marginBottom: '1rem' }}>
                        <label style={labelStyle}>Passphrase</label>
                        <input
                            type="password"
                            value={passphrase}
                            onChange={e => setPassphrase(e.target.value)}
                            onKeyDown={e => { if (e.key === 'Enter') runPassphrase() }}
                            style={inputStyle}
                            autoFocus
                            disabled={busy !== ''}
                        />
                    </div>
                )}

                {err && (
                    <div style={{ color: '#ff9f9f', fontSize: '0.85rem', marginBottom: '1rem' }}>
                        {err}
                    </div>
                )}

                <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                    <button onClick={onCancel} style={secondaryBtn} disabled={busy !== ''}>
                        Cancel
                    </button>
                    {canPasskey && (
                        <button
                            onClick={runPasskey}
                            style={primaryBtn}
                            disabled={busy !== '' || !record}
                        >
                            {busy === 'passkey' ? 'Waiting for authenticator…' : 'Unlock with passkey'}
                        </button>
                    )}
                    {canPassphrase && (
                        <button
                            onClick={runPassphrase}
                            style={primaryBtn}
                            disabled={busy !== '' || !wasm || !record}
                        >
                            {busy === 'passphrase' ? 'Unlocking…' : 'Unlock with passphrase'}
                        </button>
                    )}
                </div>
            </div>
        </div>
    )
}
